import { mediaQueryLarge, prefersReducedMotion, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { Scroller } from '@theme/scrolling';

const SCROLL_HINT_DEFAULT_DELAY = 2000;
const SCROLL_HINT_DEFAULT_DISTANCE = 10;
const SCROLL_HINT_SESSION_KEY = 'collection-scroll-hint-seen';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MediaQueryList} */
  #orientationMediaQuery = window.matchMedia('(orientation: landscape)');
  /** @type {Scroller | undefined} */
  #scrollHintScroller;
  /** @type {number | undefined} */
  #scrollHintTimer;
  #scrollHintRunning = false;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    if (this.hasAttribute('data-scroll-hint-delay')) {
      this.#scrollHintScroller = new Scroller(this, { onScroll: () => {} });
      this.addEventListener('scroll', this.#handleScrollHintScroll, { passive: true });
      this.#orientationMediaQuery.addEventListener('change', this.#handleOrientationChange);
      this.#setupScrollHint();
    }

    this.setAttribute('initialized', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupScrollHint();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    if (this.hasAttribute('data-scroll-hint-delay')) {
      this.#clearScrollHintTimer();
      this.removeEventListener('scroll', this.#handleScrollHintScroll);
      this.#orientationMediaQuery.removeEventListener('change', this.#handleOrientationChange);
      this.#scrollHintScroller?.destroy();
      this.#scrollHintScroller = undefined;
    }
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (targetElement instanceof HTMLInputElement) {
      targetElement.checked = true;
      this.#setLayout('default');
    }

    this.#setupScrollHint();
  };

  /**
   * @param {MediaQueryListEvent} event
   */
  #handleOrientationChange = ({ matches }) => {
    if (!matches) {
      this.#clearScrollHintTimer();
      return;
    }

    this.#setupScrollHint();
  };

  #handleScrollHintScroll = () => {
    if (this.scrollTop <= 0) return;
    if (this.#scrollHintRunning) return;

    sessionStorage.setItem(SCROLL_HINT_SESSION_KEY, 'true');
    this.#clearScrollHintTimer();
    this.removeEventListener('scroll', this.#handleScrollHintScroll);
  };

  #setupScrollHint() {
    if (!this.hasAttribute('data-scroll-hint-delay')) return;
    if (this.#scrollHintTimer || this.#scrollHintRunning) return;
    if (!this.#orientationMediaQuery.matches || prefersReducedMotion()) return;
    if (sessionStorage.getItem(SCROLL_HINT_SESSION_KEY)) return;
    if (this.scrollTop > 0) return;

    const delay = Number(this.dataset.scrollHintDelay);

    this.#scrollHintTimer = window.setTimeout(() => {
      this.#runScrollHint();
    }, Number.isFinite(delay) && delay > 0 ? delay : SCROLL_HINT_DEFAULT_DELAY);
  }

  async #runScrollHint() {
    this.#scrollHintTimer = undefined;

    if (!this.#scrollHintScroller) return;
    if (!this.#orientationMediaQuery.matches || prefersReducedMotion()) return;
    if (sessionStorage.getItem(SCROLL_HINT_SESSION_KEY)) return;
    if (this.scrollTop > 0) return;

    this.#scrollHintRunning = true;
    sessionStorage.setItem(SCROLL_HINT_SESSION_KEY, 'true');

    const distancePct = Number(this.dataset.scrollHintDistance);
    const distance = Math.round(
      (window.innerHeight * (Number.isFinite(distancePct) && distancePct > 0 ? distancePct : SCROLL_HINT_DEFAULT_DISTANCE)) / 100
    );

    this.#scrollHintScroller.by(distance);
    await this.#scrollHintScroller.finished;

    this.#scrollHintRunning = false;
    this.removeEventListener('scroll', this.#handleScrollHintScroll);
  }

  #clearScrollHintTimer() {
    clearTimeout(this.#scrollHintTimer);
    this.#scrollHintTimer = undefined;
  }

}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
